import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {reach} from '@hapi/hoek';
import {createHashFromUnderline} from 'utils/utils';
import {triggerEdapEvent} from 'utils/edap';
import {EmbeddedEntry} from './RichTextComponents';
import {documentToReactComponents} from '@contentful/rich-text-react-renderer';
import {BLOCKS, INLINES, MARKS} from '@contentful/rich-text-types';
import {Link} from 'react-router-dom';

import './RichText.less';

/**
 * reduceLinks
 * @param {Object} entries
 * @param {String} type
 * Create lookup object by entry ID
 */
function reduceLinks(entries, type) {
    // Type must be 'block' 'inline' or 'hyperlink'
    if (['block', 'inline', 'hyperlink'].includes(type)) {
        return entries[type].reduce((data, entry) => {
            if (entry) {
                data[entry.sys.id] = entry;
            }
            return data;
        }, {});
    }
    return {};
}

function getHeadingProps(node) {
    const id = createHashFromUnderline(node);
    return {
        id: id && id.hash,
        className: classNames({RichText__anchor: !!id}),
    };
}

function renderHeading(node, children) {
    const elements = {
        'heading-2': 'h2',
        'heading-3': 'h3',
    };
    const headingProps = getHeadingProps(node);
    return React.createElement(elements[node.nodeType], headingProps, [
        ...children,
    ]);
}

const RichText = ({richTextContent, homeRoute, domain, ...rest}) => {
    const entries = reach(richTextContent, 'links.entries');

    /**
     * Create lookups used to grab the referenced links
     */
    const blocks = reduceLinks(entries, 'block');
    const inlines = reduceLinks(entries, 'inline');
    const hyperlinks = reduceLinks(entries, 'hyperlink');
    const assets = reduceLinks(
        reach(richTextContent, 'links.assets'),
        'hyperlink',
    );

    const options = {
        renderMark: {
            [MARKS.UNDERLINE]: (text) => <span className="rt-ul">{text}</span>,
        },
        renderNode: {
            [BLOCKS.HEADING_2]: renderHeading,
            [BLOCKS.HEADING_3]: renderHeading,
            [BLOCKS.EMBEDDED_ENTRY]: (node) => {
                const block = blocks[node.data.target.sys.id];
                return (
                    <EmbeddedEntry
                        block={block}
                        homeRoute={homeRoute}
                        {...rest}
                    />
                );
            },
            [INLINES.EMBEDDED_ENTRY]: (node) => {
                const inline = inlines[node.data.target.sys.id];

                return <EmbeddedEntry inline={inline} domain={domain} />;
            },
            [INLINES.HYPERLINK]: (node, children) => {
                const target = node.data.uri.includes(domain)
                    ? '_self'
                    : '_blank';
                return (
                    <a href={node.data.uri} target={target}>
                        {children[0]}
                    </a>
                );
            },
            [INLINES.ENTRY_HYPERLINK]: (node, children) => {
                const {path} = hyperlinks[node.data.target.sys.id];
                return <Link to={homeRoute + path}>{children}</Link>;
            },
            [INLINES.ASSET_HYPERLINK]: (node, children) => {
                const {url} = assets[node.data.target.sys.id];
                return (
                    <a
                        href={url}
                        download
                        onClick={() => {
                            triggerEdapEvent('generic.click', {
                                eventcategory: 'CH Article Body',
                                eventlabel: `Download: ${url.split('/').pop()}`,
                            });
                        }}
                    >
                        {children}
                    </a>
                );
            },
        },
    };

    return (
        <div className="article-content-container" itemProp="articleBody">
            {documentToReactComponents(richTextContent.json, options)}
        </div>
    );
};

RichText.propTypes = {
    richTextContent: PropTypes.shape({
        json: PropTypes.object.isRequired,
        links: PropTypes.shape({
            entries: PropTypes.shape({
                block: PropTypes.array,
                inline: PropTypes.array,
                hyperlink: PropTypes.array,
            }).isRequired,
            assets: PropTypes.shape({
                hyperlink: PropTypes.array,
            }).isRequired,
        }).isRequired,
    }),
    homeRoute: PropTypes.string.isRequired,
    isDevice: PropTypes.bool,
    translations: PropTypes.objectOf(PropTypes.string),
};

export default RichText;
